import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import { first, takeUntil, Subject, Subscription } from 'rxjs';

import { TokensService } from '../../../shared/services/tokens.service';
import { ProfileStateService } from '../../../shared/services/profile-state.service';
import { StatsigService } from '../../../shared/services/statsig.service';

import { ButtonComponent } from 'app/shared/components/button/button.component';
import { CheckoutComponent } from 'app/components/payment/checkout/checkout.component';
import { SvgIconComponent } from '../../../shared/components/svg-icon/svg-icon.component';
import { TokenStatusComponent } from 'app/shared/components/token-status/token-status.component';
import { TokensPlanCardComponent } from '../tokens-plan-card/tokens-plan-card.component';

import {
  ITokenFeature,
  ITokenPlan,
  ITokensPackageResponse
} from 'app/shared/interfaces/token.interfaces';
import { IBillingInfo } from 'app/interfaces/profile.interfaces';
import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';
import { IPaymentResult } from '../../../interfaces/payments.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import { IToastConfig } from 'app/shared/interfaces/toast-config.interfaces';

import { EButtonTypes, EButtonSizes } from 'app/shared/enums/button.enums';
import { EPaymentType } from 'app/enums/payments.enums';
import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { ETokenPlan, ETokenType, ETokenUsageCase } from 'app/shared/enums/tokens.enums';
import { EFeatureGates } from '../../../shared/enums/feature-gates.enums';

@Component({
  selector: 'stxt-tokens-card',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CheckoutComponent,
    SvgIconComponent,
    TokenStatusComponent,
    TokensPlanCardComponent
  ],
  templateUrl: './tokens-card.component.html',
  styleUrls: ['./tokens-card.component.scss']
})
export class TokensCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() billingData: IBillingInfo[] = [];
  @Input() isEditingTokens: boolean = false;
  @Input() showCheckoutComponent: boolean = false;
  @Output() showCheckoutComponentChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showCheckoutEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('tokensStatusComponent') tokensStatusComponent: TokenStatusComponent;
  tokenIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'token-star' };
  lowBalanceSvg: ISvgConfig = { name: 'info', fill: ESvgTypes.None };
  confirmBtn: IButtonConfig = {
    text: 'Next',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Default
  };
  buyNowButton: IButtonConfig = {
    text: 'Buy now',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Small
  };
  toast: IToastConfig = {
    toastSubheading: 'Tokens have been added to your account'
  };
  allTokenFeatures: ITokenFeature[] = [
    {
      mainText: 'Custom images',
      subText: 'Generate & unlock',
      icon: { fill: ESvgTypes.None, name: 'gift' },
      price: '10',
      enabled: true
    },
    {
      mainText: 'Fine-tune creations',
      subText: 'Per regeneration',
      icon: { fill: ESvgTypes.None, name: 'heart' },
      price: '2',
      enabled: false //Manually disabled until feature is ready
    },
    {
      featureGate: EFeatureGates.VOICE_CALL,
      mainText: 'Sexy voice calls',
      subText: 'Pay-per-minute',
      icon: { fill: ESvgTypes.None, name: 'lips' },
      price: 'X',
      enabled: false
    }
  ];
  tokenFeatures: ITokenFeature[] = [];
  selectedPlan: string = ETokenPlan.Popular;
  ETokenUsageCase = ETokenUsageCase;
  EPaymentType = EPaymentType;
  plans: ITokenPlan[] = [];
  activePlan: ITokenPlan;
  tokenType: ETokenType;
  ETokenType = ETokenType;
  tokensBalance: number;
  tokensBalanceSubscription: Subscription;
  voiceFeature: boolean = this.statsigService.voiceFeature$.value;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly tokensService: TokensService,
    private readonly statsigService: StatsigService,
    private readonly profileStateService: ProfileStateService
  ) {}

  ngOnInit(): void {
    this.subscribeToTokensBalance();
    this.initPackages();
    this.initFeaturesCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isEditingTokens'] && changes['isEditingTokens'].currentValue) {
      this.showCheckoutComponent = true;
      this.showCheckoutComponentChange.emit(true);
      this.showCheckoutEvent.emit(true);
    }
  }

  initFeaturesCheck(): void {
    if (this.voiceFeature === null) {
      this.statsigService.checkFeatureGate(EFeatureGates.VOICE_CALL).then(res => {
        this.statsigService.voiceFeature$.next(res);
        this.voiceFeature = res;
        this.updateTokenFeatures(res);
      });
    } else {
      this.updateTokenFeatures(this.voiceFeature);
    }
  }

  private updateTokenFeatures(voiceEnabled: boolean): void {
    this.allTokenFeatures = this.allTokenFeatures.map(feature =>
      feature.featureGate === EFeatureGates.VOICE_CALL
        ? { ...feature, enabled: voiceEnabled }
        : feature
    );
    this.tokenFeatures = this.allTokenFeatures.filter(f => f.enabled);
  }

  initPackages(): void {
    this.profileStateService.tokenPackages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((packages: ITokensPackageResponse) => {
        if (packages && packages.data && packages.data.length > 0) {
          this.plans = packages.data;
          this.sortPlans();
          this.activePlan =
            this.plans.find(plan => plan.title === ETokenPlan.Popular) || this.plans[0];
        } else {
          this.getTokensPackage();
        }
      });
  }

  getTokensPackage(): void {
    this.tokensService
      .getTokensPackage()
      .pipe(first())
      .subscribe({
        next: (res: ITokensPackageResponse) => {
          if (!res.data) return;
          this.plans = res.data;
          this.sortPlans();
          this.activePlan =
            this.plans.find(plan => plan.title === ETokenPlan.Popular) || this.plans[0];
        }
      });
  }

  sortPlans(): void {
    this.plans.sort((a, b) => {
      if (a.title === ETokenPlan.Popular) return -1;
      if (b.title === ETokenPlan.Popular) return 1;
      return 0;
    });
  }

  updateTokensScreen(event: IPaymentResult): void {
    this.showCheckoutComponent = false;
    this.showCheckoutComponentChange.emit(false);
    this.showCheckoutEvent.emit(false);
    this.tokensStatusComponent.toggleToast(event);
    this.tokensStatusComponent.tokenType.emit();
    this.showCheckoutEvent.emit(this.showCheckoutComponent);

    if (this.tokensService.getMoreTokensEvent$.value === true) {
      setTimeout(() => this.tokensService.getMoreTokensEvent$.next(false), 5000);
    }
  }

  getTokenType(tokenType: ETokenType): void {
    this.tokenType = tokenType;
  }

  showCheckout(): void {
    this.showCheckoutComponent = !this.showCheckoutComponent;
    this.showCheckoutComponentChange.emit(this.showCheckoutComponent);
    this.showCheckoutEvent.emit(this.showCheckoutComponent);
  }

  confirmEditingTokens(selectedPlan: string): void {
    this.isEditingTokens = false;
    this.activePlan = this.plans.find(plan => plan.title === selectedPlan) || this.plans[0];
  }

  selectPlan(planTitle: string): void {
    this.selectedPlan = planTitle;
    this.activePlan = this.plans.find(plan => plan.title === planTitle) || this.plans[0];
  }

  subscribeToTokensBalance(): void {
    this.tokensBalanceSubscription = this.tokensService.tokensBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe(balance => {
        this.tokensBalance = balance;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
